import VehicleDetails from '~/components/shared/VehicleDetails';
import RequestDialogs from '~/components/shared/RequestDialogs';
import {
  ScheduleGrouping,
  ScheduleDateRow,
  ScheduleTimeRow,
} from '~/components/shared/ScheduleDetails';
import React, {useState, useEffect, useContext} from 'react';
import PropTypes from 'prop-types';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import moment from 'moment';
import isFinite from 'lodash/isFinite';
import {useNavigation} from '@react-navigation/native';
import sy from '~/styles';
import withDimensions from '~/components/hoc/with-dimensions';
import {
  Text,
  Pressable,
  Dialog,
  FormTextInput,
  Checkbox,
  Calendar,
  MONTHS_3_PAST_6_FUTURE,
  RobTaskIcon,
  Link,
  LessorPersona,
  LessorPhone,
} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {
  Row as GroupingRow,
  EditDialog as GroupingRowEdit,
} from '~/components/controls/3/Grouping';
import {
  TaskActionsheet,
  SlaActionsheet,
  DealerUserActionsheet,
} from '~/components/shared/Actionsheets';
import Format from '~/lib/format';
import {
  service_request as serviceRequestApi,
  rob as robApi,
  car as carApi,
} from '~/api/private';
import {
  STATUS_DESCRIPTIONS,
  STATUS_CODES,
  STATUS_ORDER,
} from '~/types/statuses';
import {
  isRobTaskReady,
  isRobTaskApproved,
  isRobTaskCancelled,
  isRobTaskChanged,
  isNotInvoiced,
  isArchived,
  isNotReadyForInvoicing,
  ROB_TASK_STATUS,
  ROB_NOT_INVOICED_STATUS_LABELS,
  ROB_READY_CODES,
} from '~/types/rob';
import {SERVICE_TYPE_COMPONENTS_ORDER} from '~/types/services';
import {useDispatch, useSelector} from '~/lib/hooks';
import {setAppDoneLoading, setAppIsLoading} from '~/actions';
import AddIcon from '~/images/md-icons/add/materialicons/24px.svg';
import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import BlockIcon from '~/images/md-icons/block/materialicons/24px.svg';
import ReceiptIcon from '~/images/md-icons/receipt/materialicons/24px.svg';
import ErrorIcon from '~/images/md-icons/error/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import ReceiptLongIcon from '~/images/md-icons/receipt_long/materialicons/24px.svg';
import PublishedWithChangesIcon from '~/images/md-icons/published_with_changes/materialicons/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import ServiceRequestContext from '../context';
import {
  INVOICE_DISCOUNT_LABELS,
  INVOICE_DISCOUNT_ORDER,
  INVOICE_COMPONENTS_HEADER_HEIGHT_IN_PX,
  INVOICE_COMPONENTS_FOOTER_HEIGHT_IN_PX,
} from '../constants';
import DiscountInvoiceDialog from './DiscountInvoiceDialog';
import CancelRobTaskDialog from './CancelRobTaskDialog';
import TaskSubtitle from './TaskSubtitle';
import Footer from './Footer';
import RobReasonsDialog from './RobReasonsDialog';
import RequestDetails from './RequestDetails';
import ClientFields from './ClientFields';
const InvoiceComponentShape = PropTypes.shape({
  type: PropTypes.string,
  amount: PropTypes.number,
  discount: PropTypes.number,
  lessor: PropTypes.string,
  subject: PropTypes.string,
});
const InvoiceShape = PropTypes.shape({
  amount_approved: PropTypes.number,
  amount_approved_discount: PropTypes.number,
  amount: PropTypes.number,
  invoice_number: PropTypes.string,
  bahnkick_can_invoice: PropTypes.bool,
  components: PropTypes.objectOf(InvoiceComponentShape),
});
const OverviewShape = PropTypes.shape({
  datetime: PropTypes.string,
  status: PropTypes.string,
  expected_completion_date: PropTypes.string,
  completion_date: PropTypes.string,
  damage_case_number: PropTypes.string,
  license: PropTypes.string,
  mileage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dealer_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dealer_city: PropTypes.string,
  dealer_short_name: PropTypes.string,
  car_brand: PropTypes.string,
  car_name: PropTypes.string,
  fuel: PropTypes.string,
  kvk_name: PropTypes.string,
  lessor: PropTypes.string,
  assigned_user: PropTypes.string,
  components: PropTypes.array,
  invoice_number: PropTypes.string,
  rob: PropTypes.shape({
    status: PropTypes.string,
    service_request_number: PropTypes.string,
    invoicing: PropTypes.shape({
      status: PropTypes.string,
    }),
    components: PropTypes.object,
  }),
});
const getTaskStatusCode = (overview, taskId) =>
  overview?.rob?.components[taskId]?.status.code;
const getTaskStatusFlags = (overview, taskId) => {
  const statusCode = getTaskStatusCode(overview, taskId);
  return {
    statusCode,
    approved: isRobTaskApproved(statusCode),
    cancelled: statusCode === ROB_TASK_STATUS.Cancelled,
    cancellationApproved: statusCode === ROB_TASK_STATUS.CancellationApproved,
    cancellationDenied: statusCode === ROB_TASK_STATUS.CancellationDenied,
  };
};
const isTaskCancelledState = ({
  cancelled,
  cancellationApproved,
  cancellationDenied,
}) => cancelled || cancellationApproved || cancellationDenied;
const getTaskTitle = ({main_task, component}) => {
  if (!main_task?.reference) {
    return component.name;
  }
  return main_task?.source === 'TecRMI' ? main_task.work_text : component.name;
};
const getDamageStatusDescription = (overview) => {
  if (isNotInvoiced(overview.rob?.invoicing?.status)) {
    return STATUS_DESCRIPTIONS[STATUS_CODES.NoInvoice];
  }
  if (
    overview.status === STATUS_CODES.Done &&
    isNotReadyForInvoicing(overview.rob?.invoicing?.status)
  ) {
    return STATUS_DESCRIPTIONS[STATUS_CODES.NotReadyForInvoicing];
  }
  return STATUS_DESCRIPTIONS[overview.status];
};
const getDamageMainTasks = (component) => {
  const mainTasks = component.tasks.filter((item) => item.is_main_task);
  if (!mainTasks.length) {
    return [null];
  }
  return mainTasks.sort((a, b) => {
    if (a?.rob_code === b?.rob_code) {
      return 0;
    }
    return a?.rob_code < b?.rob_code ? -1 : 1;
  });
};
const hasSlaAmountTask = (tasks) => tasks.some((item) => isFinite(item.amount));
const openDamageTaskEdit = ({
  is_sla_item,
  tasks,
  component,
  main_task,
  setShowSlaEdit,
  setShowTaskEdit,
}) => {
  if (is_sla_item) {
    setShowSlaEdit({
      task: tasks[0],
      component,
    });
    return;
  }
  setShowTaskEdit({
    main_task,
    parts: [...(main_task?.parts ?? []), ...(component.parts ?? [])],
    tasks,
    component,
  });
};
const handleDamageCheckboxPress = ({
  isTaskCancelled,
  user_settings,
  overview,
  main_task,
  component,
  onActivateComponent,
  setShowCancelRobTaskDialog,
  onDeleteComponent,
}) => {
  if (isTaskCancelled) {
    onActivateComponent(component.id, main_task?.id);
  } else if (
    !user_settings.never_show_rob_task_cancel_dialog &&
    overview?.rob?.components[main_task?.id]
  ) {
    setShowCancelRobTaskDialog(main_task);
  } else {
    onDeleteComponent(component.id, main_task?.id);
  }
  return true;
};
const renderDamageTaskAmount = ({
  task_ready,
  cancelled,
  cancellationApproved,
  cancellationDenied,
  amount,
  editable,
  onTaskPress,
}) => {
  if (task_ready) {
    return (
      <Text style={sy.smallRegular}>
        {Format.price(
          cancelled || cancellationApproved || cancellationDenied ? 0 : amount,
        )}
      </Text>
    );
  }
  return (
    <Pressable disabled={!editable && !cancelled} onPress={onTaskPress}>
      <ListIcon />
    </Pressable>
  );
};
const renderDamageTaskIndicators = ({
  has_rob_errors,
  setShowRobTaskErrorsDialog,
  rob_code,
  robComponents,
  component,
  overview,
  statusCode,
  main_task,
  setShowRobReasonsDialog,
  has_work_time_overlap,
  setShowOverlapInfoDialog,
  has_discount,
  has_markup,
  setShowMarkupInfoDialog,
}) => {
  if (overview?.rob && statusCode && !isRobTaskChanged(statusCode)) {
    return (
      <View style={[sy['flex-row'], sy['items-center'], sy['gap-1']]}>
        {has_rob_errors && (
          <Pressable
            onPress={() =>
              setShowRobTaskErrorsDialog({
                title: (
                  <Text style={[sy.largePlus]}>
                    {rob_code}&nbsp;&middot;&nbsp;
                    {robComponents[rob_code]?.description}
                  </Text>
                ),
                component_id: component.id,
              })
            }>
            <ErrorIcon width={18} height={18} fill="#972727" />
          </Pressable>
        )}
        <Pressable
          onPress={() =>
            setShowRobReasonsDialog({
              rob_code: main_task?.rob_code,
            })
          }>
          <RobTaskIcon statuses={[statusCode]} width={18} height={18} />
        </Pressable>
      </View>
    );
  }
  return (
    <View style={[sy['flex-row'], sy['items-center'], sy['gap-1']]}>
      {has_rob_errors && (
        <Pressable
          onPress={() =>
            setShowRobTaskErrorsDialog({
              title: (
                <Text style={[sy.largePlus]}>
                  {rob_code}&nbsp;&middot;&nbsp;
                  {robComponents[rob_code]?.description}
                </Text>
              ),
              component_id: component.id,
            })
          }>
          <ErrorIcon width={18} height={18} fill="#972727" />
        </Pressable>
      )}
      {has_work_time_overlap && (
        <Pressable onPress={() => setShowOverlapInfoDialog(true)}>
          <PublishedWithChangesIcon fill="#299976" width={18} height={18} />
        </Pressable>
      )}
      {has_discount && <DiscountIcon fill="#299976" width={18} height={18} />}
      {has_markup && (
        <Pressable onPress={() => setShowMarkupInfoDialog(true)}>
          <SellIcon fill="#FF6C00" width={18} height={18} />
        </Pressable>
      )}
    </View>
  );
};
const renderDamageComponentRows = ({
  components,
  overview,
  robErrors,
  robContract,
  read_only,
  editable,
  user_settings,
  robComponents,
  setShowSlaEdit,
  setShowTaskEdit,
  setShowCancelRobTaskDialog,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowOverlapInfoDialog,
  setShowMarkupInfoDialog,
  onActivateComponent,
  onDeleteComponent,
}) =>
  components?.flatMap((component_id) => {
    const component = overview?.components.find(
      (item) => item.id === component_id,
    );
    const has_rob_errors = robErrors
      .map((item) => item.component_id)
      .includes(component?.id);
    return getDamageMainTasks(component).map((main_task) => {
      const tasks = component.tasks.filter(
        (item) => item.rob_code === main_task?.rob_code,
      );
      const rob_code = main_task?.rob_code;
      const amount = tasks
        .filter((task) => task.rob_code === rob_code)
        .reduce((acc, task) => acc + task.task_amount, 0);
      const task_ready =
        isRobTaskReady(
          robContract,
          main_task,
          amount,
          overview?.rob?.components[main_task?.id],
          false,
          false,
        ) === ROB_READY_CODES.Ready;
      const is_sla_item = tasks.length ? hasSlaAmountTask(tasks) : false;
      const approved = isRobTaskApproved(
        overview?.rob?.components[main_task?.id]?.status.code,
      );
      const {statusCode, cancelled, cancellationApproved, cancellationDenied} =
        getTaskStatusFlags(overview, main_task?.id);
      const isTaskCancelled = isTaskCancelledState({
        cancelled,
        cancellationApproved,
        cancellationDenied,
      });
      const has_discount = main_task?.task_discount > 0;
      const has_markup = main_task?.task_markup > 0;
      const has_work_time_overlap = tasks.some((task) =>
        isFinite(task.calculated_work_time),
      );
      const onTaskPress = () =>
        openDamageTaskEdit({
          is_sla_item,
          tasks,
          component,
          main_task,
          setShowSlaEdit,
          setShowTaskEdit,
        });
      let taskBody = null;
      if (read_only) {
        if (approved) {
          taskBody = (
            <>
              <View
                style={[
                  sy['flex-row'],
                  sy['p-4'],
                  sy['justify-between'],
                  sy['gap-4'],
                ]}>
                <View style={[sy['flex-1'], sy['flex-row']]}>
                  <Text>{rob_code}&nbsp;&middot;&nbsp;</Text>
                  <Text>
                    {robComponents[rob_code]?.description ??
                      main_task?.description ??
                      component.name}
                  </Text>
                </View>
                <Text style={sy.smallRegular}>{Format.price(amount)}</Text>
              </View>
              <View style={[sy['px-4']]}>
                <Divider style={sy['bg-gray95']} />
              </View>
            </>
          );
        }
      } else {
        taskBody = (
          <>
            <View
              style={[
                sy['flex-row'],
                sy['flex-1'],
                sy['p-4'],
                sy['gap-4'],
                !editable && sy['view-disabled'],
              ]}>
              <Checkbox
                style={[
                  sy['justify-start'],
                  {
                    width: 'auto',
                  },
                ]}
                disabled={!editable}
                checked={
                  !cancelled && !cancellationDenied && !cancellationApproved
                }
                onPress={() =>
                  handleDamageCheckboxPress({
                    isTaskCancelled,
                    user_settings,
                    overview,
                    main_task,
                    component,
                    onActivateComponent,
                    setShowCancelRobTaskDialog,
                    onDeleteComponent,
                  })
                }
              />
              <View style={[sy['flex-1']]}>
                <Pressable
                  disabled={
                    !component ||
                    !editable ||
                    cancelled ||
                    cancellationApproved ||
                    cancellationDenied
                  }
                  onPress={onTaskPress}>
                  <View style={[sy['flex-row'], sy['justify-between']]}>
                    <View style={[sy['flex-1']]}>
                      <View style={[sy['flex-row']]}>
                        {rob_code ? (
                          <Text
                            style={[
                              (cancelled ||
                                cancellationApproved ||
                                cancellationDenied) &&
                                sy['line-through'],
                              sy.truncate,
                            ]}>
                            {rob_code}&nbsp;&middot;&nbsp;
                            {robComponents[rob_code]?.description ??
                              component.name}
                          </Text>
                        ) : (
                          <Text
                            style={[
                              (cancelled ||
                                cancellationApproved ||
                                cancellationDenied) &&
                                sy['line-through'],
                              sy.truncate,
                            ]}>
                            {getTaskTitle({
                              main_task,
                              component,
                            })}
                          </Text>
                        )}
                      </View>
                      {!!component && (
                        <TaskSubtitle
                          lessor={overview?.lessor}
                          tasks={tasks}
                          is_maintenance_item={component.name === 'Onderhoud'}
                          is_sla_item={is_sla_item}
                          ready={task_ready}
                          enabled={editable && !cancelled}
                          cancelled={cancelled}
                          cancellation_approved={cancellationApproved}
                          cancellation_denied={cancellationDenied}
                          rob_components={robComponents}
                        />
                      )}
                    </View>
                  </View>
                </Pressable>
              </View>
              {component && (
                <View style={[sy['items-end']]}>
                  {renderDamageTaskAmount({
                    task_ready,
                    cancelled,
                    cancellationApproved,
                    cancellationDenied,
                    amount,
                    editable,
                    onTaskPress,
                  })}
                  {renderDamageTaskIndicators({
                    has_rob_errors,
                    setShowRobTaskErrorsDialog,
                    rob_code,
                    robComponents,
                    component,
                    overview,
                    statusCode,
                    main_task,
                    setShowRobReasonsDialog,
                    has_work_time_overlap,
                    setShowOverlapInfoDialog,
                    has_discount,
                    has_markup,
                    setShowMarkupInfoDialog,
                  })}
                </View>
              )}
            </View>
            <View style={[sy['px-4']]}>
              <Divider style={sy['bg-gray95']} />
            </View>
          </>
        );
      }
      return (
        <React.Fragment key={main_task?.id ?? component_id}>
          {taskBody}
        </React.Fragment>
      );
    });
  });
const runDamageRequestFlow = async ({
  dispatch,
  overview,
  damage_id,
  refresh,
  setRobErrors,
  setShowRequestDialog,
}) => {
  dispatch(setAppIsLoading());
  try {
    if (overview.rob?.status) {
      const {success} = await robApi.update_request(damage_id);
      if (success) {
        await refresh();
      } else {
        const {result} = await serviceRequestApi.get(damage_id, dispatch);
        setRobErrors(result[0].rob_errors);
      }
    } else {
      setShowRequestDialog(true);
      return true;
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const runDamageRobRequest = async ({
  dispatch,
  partial_vin,
  current_mileage,
  overview,
  damage_id,
  refresh,
  setRobErrors,
  setShowRequestDialog,
  setShowRobRequestErrorsDialog,
}) => {
  if (!partial_vin || !current_mileage) {
    return;
  }
  dispatch(setAppIsLoading());
  try {
    const {replacement, ...rest} = overview;
    await serviceRequestApi.patch(damage_id, {
      ...rest,
      mileage: current_mileage,
    });
    const {success} = await robApi.request(
      damage_id,
      partial_vin,
      current_mileage,
    );
    if (success) {
      await refresh();
      setShowRequestDialog(false);
    } else {
      const {result} = await serviceRequestApi.get(damage_id, dispatch);
      setRobErrors(result[0].rob_errors);
      setShowRequestDialog(false);
      setShowRobRequestErrorsDialog(
        result[0].rob_errors?.filter((item) => !item.task_id)?.length > 0,
      );
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const runDamageInvoiceFlow = async ({
  dispatch,
  damage_id,
  overview,
  discountInvoice,
  refresh,
  setShowDiscountInvoiceDialog,
  setRobErrors,
  setShowRobRequestErrorsDialog,
}) => {
  dispatch(setAppIsLoading());
  try {
    const {success, errors} = await robApi.invoice(
      damage_id,
      overview.invoice_number,
      discountInvoice,
    );
    if (success) {
      await serviceRequestApi.change_status(damage_id, STATUS_CODES.Invoiced);
      await refresh();
      setShowDiscountInvoiceDialog(false);
    } else {
      setRobErrors(errors);
      setShowRobRequestErrorsDialog(true);
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const runDamageChangeInvoiceStatus = async ({
  dispatch,
  overview,
  onUpdateRequest,
  damage_id,
  invoiceStatus,
  setShowNoInvoiceDialog,
  setShowNoInvoiceInfoDialog,
  refresh,
}) => {
  dispatch(setAppIsLoading());
  try {
    if (overview?.invoice_number) {
      await onUpdateRequest({
        invoice_number: null,
      });
    }
    const {success} = await robApi.change_invoice_status(
      damage_id,
      invoiceStatus,
    );
    if (success) {
      setShowNoInvoiceDialog(false);
      setShowNoInvoiceInfoDialog(true);
    }
  } catch (e) {
    console.error(e);
  } finally {
    await refresh();
    dispatch(setAppDoneLoading());
  }
};
const runDamageActivateComponent = async ({
  dispatch,
  damage_id,
  component_id,
  main_task_id,
  setRobErrors,
  refresh,
}) => {
  dispatch(setAppIsLoading());
  try {
    const {success, errors} = await serviceRequestApi.activate_component(
      damage_id,
      component_id,
      main_task_id,
    );
    if (errors?.length > 0) {
      setRobErrors(errors);
    }
    if (success) {
      await refresh();
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const runDamageDeleteComponent = async ({
  dispatch,
  damage_id,
  component_id,
  main_task_id,
  refresh,
}) => {
  dispatch(setAppIsLoading());
  try {
    await serviceRequestApi.delete_component(
      damage_id,
      component_id,
      main_task_id,
    );
  } catch (e) {
    console.error(e);
  } finally {
    await refresh();
    dispatch(setAppDoneLoading());
  }
};
const buildDamageTaskPayload = ({main_task, changes}) => {
  const {
    main_task_id,
    component_id,
    rob_code,
    operation,
    reason,
    location,
    parts,
    tasks: changed_tasks,
  } = changes;
  const tasks = changed_tasks.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
  const changed_task = changed_tasks.find((t) => t.id === main_task.id);
  tasks[main_task.id] = {
    ...tasks[main_task.id],
    ...changed_task,
    id: main_task.id,
    component_id,
    rob_code,
    operation,
    reason,
    location,
    parts,
    is_damage: true,
  };
  for (const child of changed_tasks.filter((t) => t.id !== main_task.id)) {
    tasks[child.id] = {
      ...child,
      component_id,
      rob_code,
      operation,
      reason,
      location,
      warranty: null,
      leniency_amount: null,
      is_damage: null,
      parts: null,
    };
  }
  return {
    main_task_id,
    tasks: Object.values(tasks).filter(Boolean),
  };
};
const runDamageTaskEdit = async ({
  dispatch,
  changes,
  overview,
  damage_id,
  refresh,
  setShowTaskEdit,
}) => {
  dispatch(setAppIsLoading());
  try {
    const {main_task_id, tasks: changed_tasks, rob_code} = changes;
    let main_task = overview.components
      .flatMap((c) => c.tasks ?? [])
      .find((item) => item.id === main_task_id && item.is_main_task);
    if (!main_task) {
      main_task = overview.components
        .flatMap((c) => c.tasks ?? [])
        .find((item) => item.rob_code === rob_code && item.is_main_task);
    }
    if (main_task?.id !== main_task_id) {
      refresh();
      setShowTaskEdit(null);
      return;
    }
    const payload = buildDamageTaskPayload({
      main_task,
      changes: {
        ...changes,
        tasks: changed_tasks,
      },
    });
    const result = await serviceRequestApi.update_tasks(damage_id, {
      tasks: payload.tasks,
    });
    if (result.success) {
      refresh();
      setShowTaskEdit(null);
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const runDamageSlaEdit = async ({
  dispatch,
  changes,
  showSlaEdit,
  overview,
  damage_id,
  refresh,
  setShowSlaEdit,
}) => {
  dispatch(setAppIsLoading());
  try {
    const {parts, discount, markup} = changes;
    const {id: task_id} = showSlaEdit.task;
    const existing_task = overview.components
      .flatMap((c) => c.tasks ?? [])
      .find((item) => item.id === task_id);
    const result = await serviceRequestApi.update_tasks(damage_id, {
      tasks: [
        {
          ...existing_task,
          discount,
          markup,
          parts,
        },
      ],
    });
    if (result.success) {
      refresh();
      setShowSlaEdit(null);
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const compareDamageComponentIds = (overview, componentAId, componentBId) => {
  const component_a = overview?.components.find(
    (item) => item.id === componentAId,
  );
  const component_b = overview?.components.find(
    (item) => item.id === componentBId,
  );
  const task_a = component_a?.tasks.find((task) => task.is_main_task);
  const task_b = component_b?.tasks.find((task) => task.is_main_task);
  if (!task_a && !SERVICE_TYPE_COMPONENTS_ORDER.includes(component_a.name)) {
    return 1;
  }
  if (!task_b && !SERVICE_TYPE_COMPONENTS_ORDER.includes(component_b.name)) {
    return -1;
  }
  if (!task_a && !task_b) {
    return (
      SERVICE_TYPE_COMPONENTS_ORDER.indexOf(component_a.name) -
      SERVICE_TYPE_COMPONENTS_ORDER.indexOf(component_b.name)
    );
  }
  if (!task_a) {
    return -1;
  }
  if (!task_b) {
    return 1;
  }
  if (task_a.rob_code && !task_b.rob_code) {
    return -1;
  }
  if (task_b.rob_code && !task_a.rob_code) {
    return 1;
  }
  if (task_a.rob_code && task_b.rob_code) {
    return task_a.rob_code.localeCompare(task_b.rob_code);
  }
  return (task_a.description ?? task_a.work_text ?? '').localeCompare(
    task_b.description ?? task_b.work_text ?? '',
  );
};
const getDamageComponentIds = (overview) =>
  overview?.components
    .map((item) => item.id)
    .sort((a, b) => compareDamageComponentIds(overview, a, b)) ?? [];
const canIncludeDamageTaskAmount = (overview, task) => {
  if (!task.rob_code) {
    return false;
  }
  if (isRobTaskCancelled(overview?.rob?.components[task.id]?.status.code)) {
    return false;
  }
  if (!task.is_main_task) {
    const main_task = overview.components
      .flatMap((c) => c.tasks ?? [])
      .find((item) => item.is_main_task && item.rob_code === task.rob_code);
    if (
      isRobTaskCancelled(
        overview?.rob?.components[main_task.id]?.status.code,
      ) ||
      main_task.warranty
    ) {
      return false;
    }
  }
  return true;
};
const getDamageTotalAmount = (overview) => {
  if (overview?.status === STATUS_CODES.Cancelled) {
    return 0;
  }
  const taskAmount =
    overview?.components
      .flatMap((c) => c.tasks ?? [])
      ?.filter((task) => canIncludeDamageTaskAmount(overview, task))
      .reduce((acc, task) => acc + task.task_amount, 0) ?? 0;
  return taskAmount + (overview?.replacement?.sla?.amount ?? 0);
};
const useDamageTabUiState = () => {
  const [partialVin, setPartialVin] = useState(null);
  const [editVin, setEditVin] = useState({});
  const [editInvoiceNumber, setEditInvoiceNumber] = useState(null);
  const [showTaskEdit, setShowTaskEdit] = useState(null);
  const [showSlaEdit, setShowSlaEdit] = useState(null);
  const [showRobReasonsDialog, setShowRobReasonsDialog] = useState(null);
  const [showNoInvoiceDialog, setShowNoInvoiceDialog] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [showNoInvoiceInfoDialog, setShowNoInvoiceInfoDialog] = useState(false);
  const [showRobRequestErrorsDialog, setShowRobRequestErrorsDialog] =
    useState(false);
  const [showRobTaskErrorsDialog, setShowRobTaskErrorsDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCancelRobTaskDialog, setShowCancelRobTaskDialog] = useState(null);
  const [showOverlapInfoDialog, setShowOverlapInfoDialog] = useState(false);
  const [showAssignUserDialog, setShowAssignUserDialog] = useState(false);
  const [showMarkupInfoDialog, setShowMarkupInfoDialog] = useState(false);
  const [showDiscountInvoiceDialog, setShowDiscountInvoiceDialog] =
    useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [editDateTime, setEditDateTime] = useState(null);
  const [editKilometrage, setEditKilometrage] = useState(null);
  const [editDamageCaseNumber, setEditDamageCaseNumber] = useState(null);
  return {
    partial_vin: partialVin,
    setPartialVin,
    edit_vin: editVin,
    setEditVIN: setEditVin,
    edit_invoiceNumber: editInvoiceNumber,
    setEditInvoiceNumber,
    showTaskEdit,
    setShowTaskEdit,
    showSlaEdit,
    setShowSlaEdit,
    showRobReasonsDialog,
    setShowRobReasonsDialog,
    showNoInvoiceDialog,
    setShowNoInvoiceDialog,
    invoiceStatus,
    setInvoiceStatus,
    showNoInvoiceInfoDialog,
    setShowNoInvoiceInfoDialog,
    showRobRequestErrorsDialog,
    setShowRobRequestErrorsDialog,
    showRobTaskErrorsDialog,
    setShowRobTaskErrorsDialog,
    showDetails,
    setShowDetails,
    showCancelRobTaskDialog,
    setShowCancelRobTaskDialog,
    showOverlapInfoDialog,
    setShowOverlapInfoDialog,
    showAssignUserDialog,
    setShowAssignUserDialog,
    showMarkupInfoDialog,
    setShowMarkupInfoDialog,
    showDiscountInvoiceDialog,
    setShowDiscountInvoiceDialog,
    showRequestDialog,
    setShowRequestDialog,
    editDateTime,
    setEditDateTime,
    editKilometrage,
    setEditKilometrage,
    editDamageCaseNumber,
    setEditDamageCaseNumber,
  };
};
const renderDamageTaskActionSheets = ({
  showTaskEdit,
  setShowTaskEdit,
  overview,
  vin_number,
  damage_id,
  onTaskEdit,
  setShowRobReasonsDialog,
  showSlaEdit,
  setShowSlaEdit,
  onSlaEdit,
}) => (
  <>
    {showTaskEdit && (
      <TaskActionsheet
        onDismiss={() => setShowTaskEdit(null)}
        license={overview.license}
        dealer_id={overview.dealer_id}
        company_id={overview.company_id}
        lessor={overview.lessor}
        vin_number={vin_number}
        mileage={overview.mileage}
        tecrmi_enabled={overview.tecrmi_enabled}
        request_id={damage_id}
        main_task={showTaskEdit.main_task}
        tasks={showTaskEdit.tasks}
        parts={showTaskEdit.parts}
        component={showTaskEdit.component}
        rob={overview.rob?.components[showTaskEdit.main_task.id]}
        is_damage_request={true}
        onChange={onTaskEdit}
        onShowDenialReasons={() =>
          setShowRobReasonsDialog({
            component_id: showTaskEdit.component.id,
          })
        }
      />
    )}

    {showSlaEdit && (
      <SlaActionsheet
        onDismiss={() => setShowSlaEdit(null)}
        license={overview.license}
        request_id={damage_id}
        damage_id={overview.damage_request?.id}
        task_id={showSlaEdit.task.id}
        component={showSlaEdit.component}
        rob={overview.rob?.components[showSlaEdit.task.id]}
        is_damage_request={true}
        onChange={onSlaEdit}
        onShowDenialReasons={() =>
          setShowRobReasonsDialog({
            component_id: showSlaEdit.component.id,
          })
        }
      />
    )}
  </>
);
const renderDamageRequestDialogs = ({partial_vin, ...props}) => (
  <RequestDialogs
    {...props}
    partialVin={partial_vin}
    requestTitle="Schade ROB"
    RobReasonsDialog={RobReasonsDialog}
  />
);
const renderDamageSupportDialogs = ({
  showDetails,
  setShowDetails,
  damage_id,
  overview,
  total_amount,
  showCancelRobTaskDialog,
  onDeleteComponent,
  refresh,
  setShowCancelRobTaskDialog,
  showOverlapInfoDialog,
  setShowOverlapInfoDialog,
  showMarkupInfoDialog,
  setShowMarkupInfoDialog,
  showAssignUserDialog,
  setShowAssignUserDialog,
  onUpdateRequest,
  showDiscountInvoiceDialog,
  setShowDiscountInvoiceDialog,
  InvoiceServiceRequest,
}) => (
  <>
    {showDetails && (
      <RequestDetails
        onDismiss={() => setShowDetails(false)}
        damage_id={damage_id}
        lessor={overview.lessor}
        license={overview.license}
        data={overview.rob}
        tasks={overview.components.flatMap((c) => c.tasks ?? [])}
        replacement_rob_code={overview.replacement?.sla?.rob_code}
        total_amount={total_amount}
      />
    )}

    <CancelRobTaskDialog
      visible={!!showCancelRobTaskDialog}
      onDismiss={async () => {
        await refresh();
        setShowCancelRobTaskDialog(null);
      }}
      onCancel={async () => {
        onDeleteComponent(
          showCancelRobTaskDialog.component_id,
          showCancelRobTaskDialog.id,
        );
        await refresh();
        setShowCancelRobTaskDialog(null);
      }}
    />

    <Dialog
      visible={showOverlapInfoDialog}
      title="Overlapcorrectie"
      titleIcon={<PublishedWithChangesIcon fill="#299976" />}
      onDismiss={() => setShowOverlapInfoDialog(false)}>
      <View style={[sy['py-4']]}>
        <Text>
          Arbeidstijd is bijgewerkt door overlap met andere werkzaamheden in
          werkopdracht.
        </Text>
      </View>
    </Dialog>

    <Dialog
      visible={showMarkupInfoDialog}
      title="Toeslag toegepast"
      titleIcon={<SellIcon fill="#FF6C00" />}
      onDismiss={() => setShowMarkupInfoDialog(false)}>
      <View style={[sy['py-4']]}>
        <Text>Hoger bedrag door toepassing toeslag.</Text>
      </View>
    </Dialog>

    {showAssignUserDialog && (
      <DealerUserActionsheet
        visible={true}
        dealer_id={overview?.dealer_id}
        assigned_user_id={overview?.assigned_user_id}
        onDismiss={() => setShowAssignUserDialog(false)}
        onOK={async (assigned_user_id) => {
          await onUpdateRequest({
            assigned_user_id,
          });
          setShowAssignUserDialog(false);
          refresh();
        }}
      />
    )}

    {showDiscountInvoiceDialog && (
      <DiscountInvoiceDialog
        onDismiss={() => setShowDiscountInvoiceDialog(false)}
        onInvoice={(number, percentage) =>
          InvoiceServiceRequest({
            number,
            percentage,
          })
        }
      />
    )}
  </>
);
const renderDamageOverlays = ({
  showTaskEdit,
  setShowTaskEdit,
  overview,
  vin_number,
  damage_id,
  onTaskEdit,
  setShowRobReasonsDialog,
  showSlaEdit,
  setShowSlaEdit,
  onSlaEdit,
  showRequestDialog,
  setShowRequestDialog,
  partial_vin,
  setPartialVin,
  current_mileage,
  setCurrentMileage,
  onRequest,
  showRobReasonsDialog,
  robComponents,
  showNoInvoiceDialog,
  setShowNoInvoiceDialog,
  setInvoiceStatus,
  invoiceStatus,
  onChangeInvoiceStatus,
  showNoInvoiceInfoDialog,
  setShowNoInvoiceInfoDialog,
  showRobRequestErrorsDialog,
  robErrors,
  setShowRobRequestErrorsDialog,
  showRobTaskErrorsDialog,
  setShowRobTaskErrorsDialog,
  showDetails,
  setShowDetails,
  total_amount,
  showCancelRobTaskDialog,
  onDeleteComponent,
  refresh,
  setShowCancelRobTaskDialog,
  showOverlapInfoDialog,
  setShowOverlapInfoDialog,
  showMarkupInfoDialog,
  setShowMarkupInfoDialog,
  showAssignUserDialog,
  setShowAssignUserDialog,
  onUpdateRequest,
  showDiscountInvoiceDialog,
  setShowDiscountInvoiceDialog,
  InvoiceServiceRequest,
}) => (
  <>
    {renderDamageTaskActionSheets({
      showTaskEdit,
      setShowTaskEdit,
      overview,
      vin_number,
      damage_id,
      onTaskEdit,
      setShowRobReasonsDialog,
      showSlaEdit,
      setShowSlaEdit,
      onSlaEdit,
    })}
    {renderDamageRequestDialogs({
      showRequestDialog,
      setShowRequestDialog,
      partial_vin,
      setPartialVin,
      current_mileage,
      setCurrentMileage,
      onRequest,
      showRobReasonsDialog,
      overview,
      robComponents,
      showNoInvoiceDialog,
      setShowNoInvoiceDialog,
      setInvoiceStatus,
      invoiceStatus,
      onChangeInvoiceStatus,
      showNoInvoiceInfoDialog,
      setShowNoInvoiceInfoDialog,
      showRobRequestErrorsDialog,
      robErrors,
      setShowRobRequestErrorsDialog,
      showRobTaskErrorsDialog,
      setShowRobTaskErrorsDialog,
      setShowRobReasonsDialog,
    })}
    {renderDamageSupportDialogs({
      showDetails,
      setShowDetails,
      damage_id,
      overview,
      total_amount,
      showCancelRobTaskDialog,
      onDeleteComponent,
      refresh,
      setShowCancelRobTaskDialog,
      showOverlapInfoDialog,
      setShowOverlapInfoDialog,
      showMarkupInfoDialog,
      setShowMarkupInfoDialog,
      showAssignUserDialog,
      setShowAssignUserDialog,
      onUpdateRequest,
      showDiscountInvoiceDialog,
      setShowDiscountInvoiceDialog,
      InvoiceServiceRequest,
    })}
  </>
);
const DamageTabDateRow = ScheduleDateRow;
const DamageTabTimeRow = ScheduleTimeRow;
const DamageTabReadyRow = ({
  overview,
  editDateTime,
  setEditDateTime,
  onUpdateRequest,
}) =>
  overview?.status &&
  STATUS_ORDER.indexOf(overview.status) <
    STATUS_ORDER.indexOf(STATUS_CODES.Done) ? (
    <GroupingRow title="Gereed (verwacht)">
      <Text>
        {moment(overview?.expected_completion_date, 'YYYY-MM-DD').format(
          'D MMMM YYYY',
        )}
      </Text>
      <GroupingRowEdit
        title="Datum wijzigen"
        dismissable
        cancelButton
        disableOKButton={!editDateTime}
        onClose={() => setEditDateTime(null)}
        onOK={() => {
          onUpdateRequest({
            expected_completion_date: editDateTime,
          });
        }}>
        <Calendar
          months={MONTHS_3_PAST_6_FUTURE}
          initialDate={overview?.expected_completion_date}
          minDate={overview.datetime}
          date={editDateTime ?? overview?.expected_completion_date}
          onPress={setEditDateTime}
        />
      </GroupingRowEdit>
    </GroupingRow>
  ) : (
    <GroupingRow title="Gereed">
      <Text>
        {moment(overview?.completion_date, 'YYYY-MM-DD').format('D MMMM YYYY')}
      </Text>
    </GroupingRow>
  );
const DamageTabCaseNumberRow = ({
  overview,
  editDamageCaseNumber,
  setEditDamageCaseNumber,
  onUpdateDamageRequest,
}) => (
  <GroupingRow
    title="Dossiernummer"
    style={[sy['gap-4']]}
    contentStyle={{
      flex: 1,
    }}
    onEditClicked={() => setEditDamageCaseNumber(overview?.damage_case_number)}>
    <Text
      style={[
        sy.truncate,
        sy['text-right'],
        {
          display: 'block',
        },
      ]}>
      {overview.damage_case_number ?? 'Opgeven'}
    </Text>
    <GroupingRowEdit
      title="Dossiernummer"
      dismissable
      cancelButton
      onOK={async () => {
        await onUpdateDamageRequest({
          damage_case_number: editDamageCaseNumber,
        });
      }}>
      <View style={[sy['p-4']]}>
        <FormTextInput
          label="Dossiernummer"
          inputProps={{
            maxLength: 50,
          }}
          value={overview?.damage_case_number}
          onChangeText={setEditDamageCaseNumber}
        />
      </View>
    </GroupingRowEdit>
  </GroupingRow>
);
const DamageTabGeneralSection = ({
  overview,
  editable,
  editDateTime,
  setEditDateTime,
  onUpdateRequest,
  editDamageCaseNumber,
  setEditDamageCaseNumber,
  onUpdateDamageRequest,
}) => (
  <ScheduleGrouping overview={overview}>
    <DamageTabDateRow
      overview={overview}
      editable={editable}
      editDateTime={editDateTime}
      setEditDateTime={setEditDateTime}
      onUpdateRequest={onUpdateRequest}
    />
    <DamageTabTimeRow
      overview={overview}
      editable={editable}
      editDateTime={editDateTime}
      setEditDateTime={setEditDateTime}
      onUpdateRequest={onUpdateRequest}
    />
    <DamageTabReadyRow
      overview={overview}
      editDateTime={editDateTime}
      setEditDateTime={setEditDateTime}
      onUpdateRequest={onUpdateRequest}
    />
    <DamageTabCaseNumberRow
      overview={overview}
      editDamageCaseNumber={editDamageCaseNumber}
      setEditDamageCaseNumber={setEditDamageCaseNumber}
      onUpdateDamageRequest={onUpdateDamageRequest}
    />
    {overview.rob && (
      <GroupingRow title="Opdrachtnummer">
        <Text>{overview.rob?.service_request_number ?? '-'}</Text>
      </GroupingRow>
    )}
  </ScheduleGrouping>
);
const DamageTabCarSection = ({edit_vin, setEditVIN, ...props}) => (
  <VehicleDetails {...props} editVin={edit_vin} setEditVin={setEditVIN} />
);
const DamageTabLessorSection = ({
  overview,
  robContract,
  contract_ends,
  onUpdateRequest,
}) => (
  <Grouping
    icon={
      <LessorPersona
        lessor={overview?.lessor}
        fallback={
          <BusinessIcon
            fill={
              contract_ends || robContract?.isActive === false
                ? '#972727'
                : '#4A4A49'
            }
          />
        }
        size={24}
      />
    }
    header={overview?.kvk_name ?? overview?.lessor}>
    {robContract?.lessor?.sublessor && (
      <GroupingRow
        title="Subleasemaatschappij"
        style={[sy['gap-4']]}
        contentStyle={[sy['flex-1']]}>
        <Text style={[sy['text-right']]}>
          {robContract.lessor.sublessor.name}
        </Text>
      </GroupingRow>
    )}
    {robContract?.lessor && (
      <GroupingRow
        title="Telefoonnummer"
        style={[sy['gap-4']]}
        contentStyle={[sy['flex-1']]}>
        <LessorPhone lessor={robContract?.lessor} style={[sy['text-right']]} />
      </GroupingRow>
    )}
    <ClientFields overview={overview} onUpdateRequest={onUpdateRequest} />
  </Grouping>
);
const DamageTabComponentSection = ({
  overview,
  components,
  robErrors,
  robContract,
  read_only,
  editable,
  user_settings,
  robComponents,
  setShowSlaEdit,
  setShowTaskEdit,
  setShowCancelRobTaskDialog,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowOverlapInfoDialog,
  setShowMarkupInfoDialog,
  onActivateComponent,
  onDeleteComponent,
  navigation,
  damage_id,
  setShowDetails,
  setShowAssignUserDialog,
  total_amount,
}) => (
  <View style={[sy['bg-white'], sy['overflow-hidden'], sy['rounded-lg']]}>
    <View style={[sy['flex-row'], sy['justify-between'], sy['p-4']]}>
      <View style={[sy['flex-row'], sy['flex-1'], sy['gap-4']]}>
        <AssignmentIcon fill="#4A4A49" />
        <View>
          <Text style={sy.mediumPlus}>
            Opdracht&nbsp;&middot;&nbsp;{getDamageStatusDescription(overview)}
          </Text>
          <Link
            private
            textStyle={[sy.small, sy['text-lightgray']]}
            onPress={() => setShowAssignUserDialog(true)}>
            {overview?.assigned_user ?? 'Toewijzen'}
          </Link>
        </View>
      </View>
      <Text style={sy.mediumPlus}>{Format.price(total_amount)}</Text>
    </View>
    <Divider style={sy['bg-gray88']} />
    {renderDamageComponentRows({
      components,
      overview,
      robErrors,
      robContract,
      read_only,
      editable,
      user_settings,
      robComponents,
      setShowSlaEdit,
      setShowTaskEdit,
      setShowCancelRobTaskDialog,
      setShowRobTaskErrorsDialog,
      setShowRobReasonsDialog,
      setShowOverlapInfoDialog,
      setShowMarkupInfoDialog,
      onActivateComponent,
      onDeleteComponent,
    })}

    {read_only ? (
      <Pressable
        style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}
        onPress={() => setShowDetails(true)}>
        <ReceiptLongIcon />
        <Text style={[sy.underline]}>Details</Text>
      </Pressable>
    ) : (
      <View
        style={[
          sy['p-4'],
          sy['flex-row'],
          sy['justify-between'],
          !editable && sy['view-disabled'],
        ]}>
        <Pressable
          style={[sy['flex-row'], sy['gap-4']]}
          disabled={!editable}
          onPress={() =>
            navigation.navigate('AddTasks', {
              damage_id,
              license: overview?.license,
            })
          }>
          <AddIcon fill="#231fda" />
          <Text>Item toevoegen</Text>
        </Pressable>
      </View>
    )}
  </View>
);
const DamageTabInvoiceSection = (props) => (
  <DamageTabInvoiceSectionBody {...props} />
);
const DamageTabInvoiceSectionBody = ({
  overview,
  invoice,
  total_amount,
  dimensions,
  edit_invoiceNumber,
  setEditInvoiceNumber,
  onUpdateRequest,
  onDoRequest,
  setShowNoInvoiceDialog,
}) => {
  if (
    !(
      [STATUS_CODES.Done, STATUS_CODES.DoneNoInvoice].includes(
        overview.status,
      ) || overview.rob?.invoicing?.status === STATUS_CODES.Invoiced
    )
  ) {
    return null;
  }
  return (
    <View style={[sy['bg-white'], sy['rounded-lg'], sy['overflow-hidden']]}>
      <DamageTabInvoiceHeader
        overview={overview}
        invoice={invoice}
        total_amount={total_amount}
      />
      <Divider style={sy['bg-gray88']} />
      <DamageTabInvoiceSectionContent
        overview={overview}
        invoice={invoice}
        total_amount={total_amount}
        dimensions={dimensions}
        edit_invoiceNumber={edit_invoiceNumber}
        setEditInvoiceNumber={setEditInvoiceNumber}
        onUpdateRequest={onUpdateRequest}
        onDoRequest={onDoRequest}
        setShowNoInvoiceDialog={setShowNoInvoiceDialog}
      />
    </View>
  );
};
const DamageTabInvoiceSectionContent = ({
  overview,
  invoice,
  total_amount,
  dimensions,
  edit_invoiceNumber,
  setEditInvoiceNumber,
  onUpdateRequest,
  onDoRequest,
  setShowNoInvoiceDialog,
}) => {
  const invoicingStatus = overview.rob?.invoicing?.status;
  const notInvoiced = isNotInvoiced(invoicingStatus);
  const archived = isArchived(overview.rob?.status);
  return (
    <View style={[sy['bg-white']]}>
      {notInvoiced ? (
        <DamageTabInvoiceNotInvoicedReason overview={overview} />
      ) : (
        <DamageTabInvoiceInvoicedContent
          overview={overview}
          invoice={invoice}
          total_amount={total_amount}
          dimensions={dimensions}
          edit_invoiceNumber={edit_invoiceNumber}
          setEditInvoiceNumber={setEditInvoiceNumber}
          onUpdateRequest={onUpdateRequest}
        />
      )}
      <GroupingRow
        title={
          <View>
            <Text>Totaal</Text>
            <Text style={[sy.smallRegular, sy['text-lightgray']]}>
              Exclusief BTW
            </Text>
          </View>
        }>
        <Text style={sy.smallRegular}>
          {Format.price(invoice?.amount ?? total_amount)}
        </Text>
      </GroupingRow>
      {!archived && (
        <DamageTabInvoiceActions
          overview={overview}
          invoice={invoice}
          setShowNoInvoiceDialog={setShowNoInvoiceDialog}
          onDoRequest={onDoRequest}
        />
      )}
    </View>
  );
};
const DamageTabInvoiceInvoicedContent = ({
  overview,
  invoice,
  total_amount,
  dimensions,
  edit_invoiceNumber,
  setEditInvoiceNumber,
  onUpdateRequest,
}) => (
  <>
    <DamageTabInvoiceNumberRow
      overview={overview}
      invoice={invoice}
      edit_invoiceNumber={edit_invoiceNumber}
      setEditInvoiceNumber={setEditInvoiceNumber}
      onUpdateRequest={onUpdateRequest}
    />
    {invoice?.components && (
      <DamageTabInvoiceComponents
        invoice={invoice}
        dimensions={dimensions}
        total_amount={total_amount}
      />
    )}
  </>
);
const DamageTabInvoiceHeader = ({overview, invoice, total_amount}) => {
  const invoicingStatus = overview.rob?.invoicing?.status;
  const iconFill =
    !isNotInvoiced(invoicingStatus) &&
    !overview.invoice_number &&
    !invoice?.bahnkick_can_invoice
      ? '#FF6C00'
      : '#4A4A49';
  return (
    <View
      style={[
        sy['flex-row'],
        sy['items-center'],
        sy['justify-between'],
        sy['p-4'],
      ]}>
      <View style={[sy['flex-row'], sy['flex-1'], sy['gap-4']]}>
        <ReceiptIcon fill={iconFill} />
        <Text style={sy.mediumPlus}>
          {isNotInvoiced(invoicingStatus) ? (
            <>Geen factuur</>
          ) : (
            <>
              Factuur
              {overview.invoice_number ? (
                <>&nbsp;&middot;&nbsp;{overview.invoice_number}</>
              ) : null}
            </>
          )}
        </Text>
      </View>
      {isNotInvoiced(invoicingStatus) ? null : (
        <Text style={sy.mediumPlus}>
          {Format.price(invoice?.amount ?? total_amount)}
        </Text>
      )}
    </View>
  );
};
const DamageTabInvoiceNotInvoicedReason = ({overview}) => (
  <GroupingRow title="Reden">
    <Text>
      {ROB_NOT_INVOICED_STATUS_LABELS[overview.rob?.invoicing?.status]}
    </Text>
  </GroupingRow>
);
const DamageTabInvoiceNumberRow = ({
  overview,
  invoice,
  edit_invoiceNumber,
  setEditInvoiceNumber,
  onUpdateRequest,
}) => {
  const canGenerateInvoice = invoice?.bahnkick_can_invoice;
  const linkStyle =
    !overview.invoice_number && !canGenerateInvoice
      ? [
          {
            color: '#FF6C00',
          },
        ]
      : null;
  return (
    <GroupingRow
      linkStyle={linkStyle}
      title={
        <View>
          <View style={[sy['flex-row'], sy['items-center']]}>
            <Text>Nummer</Text>
          </View>
        </View>
      }
      onEditClicked={
        canGenerateInvoice
          ? null
          : () =>
              setEditInvoiceNumber({
                value: overview?.invoice_number,
                valid: true,
              })
      }>
      {canGenerateInvoice && !overview.invoice_number ? (
        <Text style={sy.smallRegular}>(Wordt gegenereerd)</Text>
      ) : (
        overview.invoice_number ?? <>Opgeven</>
      )}
      {canGenerateInvoice ? null : (
        <GroupingRowEdit
          title="Factuur"
          dismissable
          cancelButton
          onValidate={async () => {
            if (edit_invoiceNumber.value?.length > 0) {
              const result = await onUpdateRequest({
                invoice_number: edit_invoiceNumber.value?.trim(),
              });
              if (result) {
                return true;
              }
              setEditInvoiceNumber((prevState) => ({
                ...prevState,
                valid: false,
              }));
              return false;
            }
          }}>
          <View style={[sy['p-4']]}>
            <FormTextInput
              label="Nummer"
              value={edit_invoiceNumber?.value}
              hasError={edit_invoiceNumber?.valid === false}
              errorMessage="Factuurnummer opslaan is mislukt. Controleer het factuurnummer en probeer het opnieuw."
              onEndEditing={(value) => {
                setEditInvoiceNumber((prevState) => ({
                  ...prevState,
                  value,
                  valid: true,
                }));
              }}
            />
          </View>
        </GroupingRowEdit>
      )}
    </GroupingRow>
  );
};
const DamageTabInvoiceComponents = ({invoice, dimensions, total_amount}) => {
  const amountApproved = invoice.amount_approved ?? 0;
  const amountApprovedDiscount = invoice.amount_approved_discount ?? 0;
  const invoiceAmount = invoice.amount ?? total_amount;
  const withholdingAmount = Format.round(
    amountApproved - amountApprovedDiscount - invoiceAmount,
  );
  return (
    <>
      <GroupingRow
        title={
          <View>
            <Text>Opdracht</Text>
            <Text style={[sy.smallRegular, sy['text-lightgray']]}>
              Exclusief BTW
            </Text>
          </View>
        }>
        <Text style={sy.smallRegular}>{Format.price(amountApproved)}</Text>
      </GroupingRow>
      {withholdingAmount > 0 && (
        <GroupingRow
          title={
            <View>
              <Text>Inhouding</Text>
            </View>
          }>
          <Text style={sy.smallRegular}>{Format.price(withholdingAmount)}</Text>
          <GroupingRowEdit
            title="Inhouding"
            titleIcon={<DiscountIcon fill="#299976" />}
            titleStyle={[sy['ml-4'], sy['mr-4']]}>
            <InvoiceComponentRows
              invoice={invoice}
              dimensions={dimensions}
              filter={(type) => invoice.components[type].amount > 0}
            />
          </GroupingRowEdit>
        </GroupingRow>
      )}
      {amountApprovedDiscount > 0 && (
        <GroupingRow title="Korting">
          <Text style={sy.smallRegular}>
            {Format.price(amountApprovedDiscount)}
          </Text>
          <GroupingRowEdit
            title="Korting"
            titleIcon={<DiscountIcon fill="#299976" />}
            titleStyle={[sy['ml-4'], sy['mr-4']]}>
            <InvoiceComponentRows
              invoice={invoice}
              dimensions={dimensions}
              filter={(type) =>
                [
                  'SLA_APPROVED_INVOICE_DISCOUNT',
                  'SLA_APPROVED_INVOICE_NEW_TIRES_DISCOUNT',
                ].includes(type) && invoice.components[type].amount > 0
              }
            />
          </GroupingRowEdit>
        </GroupingRow>
      )}
    </>
  );
};
const InvoiceComponentRows = ({invoice, dimensions, filter}) => (
  <ScrollView
    contentContainerStyle={{
      maxHeight:
        dimensions.window.height -
        INVOICE_COMPONENTS_HEADER_HEIGHT_IN_PX -
        INVOICE_COMPONENTS_FOOTER_HEIGHT_IN_PX,
    }}>
    {Object.keys(invoice.components)
      .filter((type) => filter(type))
      .sort(
        (a, b) =>
          INVOICE_DISCOUNT_ORDER.indexOf(a) - INVOICE_DISCOUNT_ORDER.indexOf(b),
      )
      .map((type) => {
        const item = invoice.components[type];
        return <InvoiceComponentRow key={type} item={item} />;
      })}
  </ScrollView>
);
const InvoiceComponentRow = ({item}) => (
  <GroupingRow
    title={
      <View>
        <Text>{INVOICE_DISCOUNT_LABELS[item.type] ?? <>Korting</>}</Text>
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          {[
            'DEALER_FEE_AMOUNT',
            'DEALER_TECRMI_COST',
            'DEALER_SMS_COST',
          ].includes(item.type) ? (
            Format.price(item.amount)
          ) : (
            <>
              {Format.number(item.discount * 100, 1, false, {
                minimumFractionDigits: 0,
              })}
              %
            </>
          )}
          {item.lessor && (
            <>
              &nbsp;&middot;&nbsp;
              {item.lessor}
            </>
          )}
          {item.subject && (
            <>
              &nbsp;&middot;&nbsp;
              {item.subject}
            </>
          )}
        </Text>
      </View>
    }>
    <Text style={sy.smallRegular}>{Format.price(item.amount)}</Text>
  </GroupingRow>
);
const DamageTabInvoiceActions = ({
  overview,
  invoice,
  setShowNoInvoiceDialog,
  onDoRequest,
}) => {
  const invoicingStatus = overview.rob?.invoicing?.status;
  return (
    <>
      {invoicingStatus === 'ReadyForInvoicing' && (
        <View style={[sy['p-4'], sy['flex-row']]}>
          <BlockIcon />
          <Link
            style={[sy['pl-8']]}
            onPress={() => setShowNoInvoiceDialog(true)}>
            Niet factureren
          </Link>
        </View>
      )}
      {isNotInvoiced(invoicingStatus) && (
        <View style={[sy['p-4'], sy['flex-row']]}>
          <ReceiptIcon />
          <Link style={[sy['pl-8']]} onPress={async () => onDoRequest()}>
            Wel factureren
          </Link>
        </View>
      )}
    </>
  );
};
DamageTabInvoiceSection.propTypes = {
  overview: OverviewShape,
  invoice: InvoiceShape,
  total_amount: PropTypes.number,
  dimensions: PropTypes.object,
  edit_invoiceNumber: PropTypes.object,
  setEditInvoiceNumber: PropTypes.func,
  onUpdateRequest: PropTypes.func,
  onDoRequest: PropTypes.func,
  setShowNoInvoiceDialog: PropTypes.func,
};
DamageTabInvoiceSectionBody.propTypes = {
  overview: OverviewShape.isRequired,
  invoice: InvoiceShape,
  total_amount: PropTypes.number,
  dimensions: PropTypes.object,
  edit_invoiceNumber: PropTypes.object,
  setEditInvoiceNumber: PropTypes.func,
  onUpdateRequest: PropTypes.func,
  onDoRequest: PropTypes.func,
  setShowNoInvoiceDialog: PropTypes.func,
};
DamageTabInvoiceSectionContent.propTypes = {
  overview: OverviewShape.isRequired,
  invoice: InvoiceShape,
  total_amount: PropTypes.number,
  dimensions: PropTypes.object,
  edit_invoiceNumber: PropTypes.object,
  setEditInvoiceNumber: PropTypes.func,
  onUpdateRequest: PropTypes.func,
  onDoRequest: PropTypes.func,
  setShowNoInvoiceDialog: PropTypes.func,
};
DamageTabInvoiceInvoicedContent.propTypes = {
  overview: OverviewShape.isRequired,
  invoice: InvoiceShape,
  total_amount: PropTypes.number,
  dimensions: PropTypes.object,
  edit_invoiceNumber: PropTypes.object,
  setEditInvoiceNumber: PropTypes.func,
  onUpdateRequest: PropTypes.func,
};
DamageTabInvoiceHeader.propTypes = {
  overview: OverviewShape.isRequired,
  invoice: InvoiceShape,
  total_amount: PropTypes.number,
};
DamageTabInvoiceNotInvoicedReason.propTypes = {
  overview: OverviewShape.isRequired,
};
DamageTabInvoiceNumberRow.propTypes = {
  overview: OverviewShape.isRequired,
  invoice: InvoiceShape,
  edit_invoiceNumber: PropTypes.object,
  setEditInvoiceNumber: PropTypes.func,
  onUpdateRequest: PropTypes.func,
};
DamageTabInvoiceComponents.propTypes = {
  invoice: InvoiceShape.isRequired,
  dimensions: PropTypes.object,
  total_amount: PropTypes.number,
};
InvoiceComponentRows.propTypes = {
  invoice: InvoiceShape.isRequired,
  dimensions: PropTypes.object,
  filter: PropTypes.func.isRequired,
};
InvoiceComponentRow.propTypes = {
  item: InvoiceComponentShape.isRequired,
};
DamageTabInvoiceActions.propTypes = {
  overview: OverviewShape.isRequired,
  invoice: InvoiceShape,
  setShowNoInvoiceDialog: PropTypes.func,
  onDoRequest: PropTypes.func,
};
DamageTabDateRow.propTypes = {
  overview: OverviewShape.isRequired,
  editable: PropTypes.bool,
  editDateTime: PropTypes.object,
  setEditDateTime: PropTypes.func,
  onUpdateRequest: PropTypes.func,
};
DamageTabTimeRow.propTypes = {
  overview: OverviewShape.isRequired,
  editable: PropTypes.bool,
  editDateTime: PropTypes.object,
  setEditDateTime: PropTypes.func,
  onUpdateRequest: PropTypes.func,
};
DamageTabReadyRow.propTypes = {
  overview: OverviewShape.isRequired,
  editDateTime: PropTypes.object,
  setEditDateTime: PropTypes.func,
  onUpdateRequest: PropTypes.func,
};
DamageTabCaseNumberRow.propTypes = {
  overview: OverviewShape.isRequired,
  editDamageCaseNumber: PropTypes.any,
  setEditDamageCaseNumber: PropTypes.func,
  onUpdateDamageRequest: PropTypes.func,
};
DamageTabGeneralSection.propTypes = {
  overview: OverviewShape.isRequired,
  editable: PropTypes.bool,
  editDateTime: PropTypes.object,
  setEditDateTime: PropTypes.func,
  onUpdateRequest: PropTypes.func,
  editDamageCaseNumber: PropTypes.any,
  setEditDamageCaseNumber: PropTypes.func,
  onUpdateDamageRequest: PropTypes.func,
};
DamageTabCarSection.propTypes = {
  overview: OverviewShape.isRequired,
  vin_part: PropTypes.string,
  vin_number: PropTypes.string,
  edit_vin: PropTypes.object,
  setEditVIN: PropTypes.func,
  car: PropTypes.object,
  editKilometrage: PropTypes.any,
  setEditKilometrage: PropTypes.func,
  setCar: PropTypes.func,
  onUpdateRequest: PropTypes.func,
  onUpdateCar: PropTypes.func,
  refresh: PropTypes.func,
};
DamageTabLessorSection.propTypes = {
  overview: OverviewShape.isRequired,
  robContract: PropTypes.object,
  contract_ends: PropTypes.bool,
  onUpdateRequest: PropTypes.func,
};
DamageTabComponentSection.propTypes = {
  overview: OverviewShape.isRequired,
  components: PropTypes.array,
  robErrors: PropTypes.array,
  robContract: PropTypes.object,
  read_only: PropTypes.bool,
  editable: PropTypes.bool,
  user_settings: PropTypes.object,
  robComponents: PropTypes.object,
  setShowSlaEdit: PropTypes.func,
  setShowTaskEdit: PropTypes.func,
  setShowCancelRobTaskDialog: PropTypes.func,
  setShowRobTaskErrorsDialog: PropTypes.func,
  setShowRobReasonsDialog: PropTypes.func,
  setShowOverlapInfoDialog: PropTypes.func,
  setShowMarkupInfoDialog: PropTypes.func,
  onActivateComponent: PropTypes.func,
  onDeleteComponent: PropTypes.func,
  navigation: PropTypes.object,
  damage_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setShowDetails: PropTypes.func,
  setShowAssignUserDialog: PropTypes.func,
  total_amount: PropTypes.number,
};
const DamageTabScrollContent = (props) => {
  const {
    dimensions,
    overview,
    editable,
    read_only,
    vin_part,
    vin_number,
    edit_vin,
    setEditVIN,
    car,
    editKilometrage,
    setEditKilometrage,
    onUpdateRequest,
    onUpdateDamageRequest,
    onUpdateCar,
    setCar,
    edit_invoiceNumber,
    setEditInvoiceNumber,
    editDateTime,
    setEditDateTime,
    editDamageCaseNumber,
    setEditDamageCaseNumber,
    robComponents,
    setShowNoInvoiceDialog,
    robErrors,
    setShowRobTaskErrorsDialog,
    setShowRobReasonsDialog,
    components,
    total_amount,
    robContract,
    invoice,
    user_settings,
    setShowSlaEdit,
    setShowTaskEdit,
    setShowCancelRobTaskDialog,
    setShowOverlapInfoDialog,
    setShowMarkupInfoDialog,
    setShowDetails,
    setShowAssignUserDialog,
    onActivateComponent,
    onDeleteComponent,
    navigation,
    damage_id,
    onDoRequest,
    contract_ends,
    refresh,
  } = props;
  return (
    <ScrollView
      style={[sy['bg-gray95']]}
      contentContainerStyle={[sy['p-2'], sy['gap-2'], sy['mb-14']]}>
      {renderDamageOverlays(props)}
      <DamageTabGeneralSection
        overview={overview}
        editable={editable}
        editDateTime={editDateTime}
        setEditDateTime={setEditDateTime}
        onUpdateRequest={onUpdateRequest}
        editDamageCaseNumber={editDamageCaseNumber}
        setEditDamageCaseNumber={setEditDamageCaseNumber}
        onUpdateDamageRequest={onUpdateDamageRequest}
      />
      <DamageTabCarSection
        overview={overview}
        vin_part={vin_part}
        vin_number={vin_number}
        edit_vin={edit_vin}
        setEditVIN={setEditVIN}
        car={car}
        editKilometrage={editKilometrage}
        setEditKilometrage={setEditKilometrage}
        setCar={setCar}
        onUpdateRequest={onUpdateRequest}
        onUpdateCar={onUpdateCar}
        refresh={refresh}
      />
      <DamageTabLessorSection
        overview={overview}
        robContract={robContract}
        contract_ends={contract_ends}
        onUpdateRequest={onUpdateRequest}
      />
      <DamageTabComponentSection
        overview={overview}
        components={components}
        robErrors={robErrors}
        robContract={robContract}
        read_only={read_only}
        editable={editable}
        user_settings={user_settings}
        robComponents={robComponents}
        setShowSlaEdit={setShowSlaEdit}
        setShowTaskEdit={setShowTaskEdit}
        setShowCancelRobTaskDialog={setShowCancelRobTaskDialog}
        setShowRobTaskErrorsDialog={setShowRobTaskErrorsDialog}
        setShowRobReasonsDialog={setShowRobReasonsDialog}
        setShowOverlapInfoDialog={setShowOverlapInfoDialog}
        setShowMarkupInfoDialog={setShowMarkupInfoDialog}
        onActivateComponent={onActivateComponent}
        onDeleteComponent={onDeleteComponent}
        navigation={navigation}
        damage_id={damage_id}
        setShowDetails={setShowDetails}
        setShowAssignUserDialog={setShowAssignUserDialog}
        total_amount={total_amount}
      />
      <DamageTabInvoiceSection
        overview={overview}
        invoice={invoice}
        total_amount={total_amount}
        dimensions={dimensions}
        edit_invoiceNumber={edit_invoiceNumber}
        setEditInvoiceNumber={setEditInvoiceNumber}
        onUpdateRequest={onUpdateRequest}
        onDoRequest={onDoRequest}
        setShowNoInvoiceDialog={setShowNoInvoiceDialog}
      />
    </ScrollView>
  );
};
const DamageTabBody = ({dimensions, focused, onChangeTab}) => {
  const navigation = useNavigation();
  const {
    partial_vin,
    setPartialVin,
    edit_vin,
    setEditVIN,
    edit_invoiceNumber,
    setEditInvoiceNumber,
    showTaskEdit,
    setShowTaskEdit,
    showSlaEdit,
    setShowSlaEdit,
    showRobReasonsDialog,
    setShowRobReasonsDialog,
    showNoInvoiceDialog,
    setShowNoInvoiceDialog,
    invoiceStatus,
    setInvoiceStatus,
    showNoInvoiceInfoDialog,
    setShowNoInvoiceInfoDialog,
    showRobRequestErrorsDialog,
    setShowRobRequestErrorsDialog,
    showRobTaskErrorsDialog,
    setShowRobTaskErrorsDialog,
    showDetails,
    setShowDetails,
    showCancelRobTaskDialog,
    setShowCancelRobTaskDialog,
    showOverlapInfoDialog,
    setShowOverlapInfoDialog,
    showAssignUserDialog,
    setShowAssignUserDialog,
    showMarkupInfoDialog,
    setShowMarkupInfoDialog,
    showDiscountInvoiceDialog,
    setShowDiscountInvoiceDialog,
    showRequestDialog,
    setShowRequestDialog,
    editDateTime,
    setEditDateTime,
    editKilometrage,
    setEditKilometrage,
    editDamageCaseNumber,
    setEditDamageCaseNumber,
  } = useDamageTabUiState();
  const dispatch = useDispatch();
  const user_settings = useSelector((state) => state.user_settings);
  const {
    damage_id,
    overview,
    editable,
    read_only,
    robComponents,
    invoice,
    vin_part,
    vin_number,
    robContract,
    robErrors,
    car,
    current_mileage,
    refresh,
    setCar,
    setCurrentMileage,
    setRobErrors,
  } = useContext(ServiceRequestContext);
  useEffect(() => {
    if (focused) {
      refresh();
    }
  }, [focused, refresh]);
  useEffect(() => {
    setPartialVin(vin_part);
  }, [vin_part, setPartialVin]);
  const onRequest = async () =>
    runDamageRobRequest({
      dispatch,
      partial_vin,
      current_mileage,
      overview,
      damage_id,
      refresh,
      setRobErrors,
      setShowRequestDialog,
      setShowRobRequestErrorsDialog,
    });
  const InvoiceServiceRequest = async (discountInvoice) =>
    runDamageInvoiceFlow({
      dispatch,
      damage_id,
      overview,
      discountInvoice,
      refresh,
      setShowDiscountInvoiceDialog,
      setRobErrors,
      setShowRobRequestErrorsDialog,
    });
  const onInvoice = async () => {
    if (
      robContract?.supplierContract?.damage
        ?.firstDebitInvoiceRequiresCreditDiscountInvoice
    ) {
      setShowDiscountInvoiceDialog(true);
    } else {
      await InvoiceServiceRequest();
    }
  };
  const onDoRequest = async () => {
    await runDamageRequestFlow({
      dispatch,
      overview,
      damage_id,
      refresh,
      setRobErrors,
      setShowRequestDialog,
    });
  };
  const onChangeInvoiceStatus = async () =>
    runDamageChangeInvoiceStatus({
      dispatch,
      overview,
      onUpdateRequest,
      damage_id,
      invoiceStatus,
      setShowNoInvoiceDialog,
      setShowNoInvoiceInfoDialog,
      refresh,
    });
  const onActivateComponent = async (component_id, main_task_id) =>
    runDamageActivateComponent({
      dispatch,
      damage_id,
      component_id,
      main_task_id,
      setRobErrors,
      refresh,
    });
  const onDeleteComponent = async (component_id, main_task_id) =>
    runDamageDeleteComponent({
      dispatch,
      damage_id,
      component_id,
      main_task_id,
      refresh,
    });
  const onUpdateRequest = async (changes) => {
    const {replacement, ...rest} = overview;
    const result = await serviceRequestApi.patch(damage_id, {
      ...rest,
      ...changes,
    });
    if (result?.success) {
      refresh();
    }
    return result?.success;
  };
  const onUpdateDamageRequest = async (changes) => {
    const result = await serviceRequestApi.damage_patch(damage_id, {
      ...changes,
    });
    if (result?.success) {
      refresh();
    }
    return result?.success;
  };
  const onUpdateCar = async (attributes) => {
    const result = await carApi.change_attributes(car.license, attributes);
    if (result?.success) {
      refresh();
    }
  };
  const onTaskEdit = async (changes) =>
    runDamageTaskEdit({
      dispatch,
      changes,
      overview,
      damage_id,
      refresh,
      setShowTaskEdit,
    });
  const onSlaEdit = async (changes) =>
    runDamageSlaEdit({
      dispatch,
      changes,
      showSlaEdit,
      overview,
      damage_id,
      refresh,
      setShowSlaEdit,
    });
  const components = getDamageComponentIds(overview);
  const total_amount = getDamageTotalAmount(overview);
  const contract_ends =
    robContract?.leaseContract?.endDate &&
    moment(robContract?.leaseContract?.endDate).isBefore(
      moment().add(3, 'months'),
    );
  if (!overview) {
    return <></>;
  }
  return (
    <>
      <DamageTabScrollContent
        dimensions={dimensions}
        overview={overview}
        editable={editable}
        read_only={read_only}
        vin_part={vin_part}
        vin_number={vin_number}
        edit_vin={edit_vin}
        setEditVIN={setEditVIN}
        car={car}
        editKilometrage={editKilometrage}
        setEditKilometrage={setEditKilometrage}
        onUpdateRequest={onUpdateRequest}
        onUpdateDamageRequest={onUpdateDamageRequest}
        onUpdateCar={onUpdateCar}
        setCar={setCar}
        edit_invoiceNumber={edit_invoiceNumber}
        setEditInvoiceNumber={setEditInvoiceNumber}
        editDateTime={editDateTime}
        setEditDateTime={setEditDateTime}
        editDamageCaseNumber={editDamageCaseNumber}
        setEditDamageCaseNumber={setEditDamageCaseNumber}
        showRequestDialog={showRequestDialog}
        setShowRequestDialog={setShowRequestDialog}
        partial_vin={partial_vin}
        setPartialVin={setPartialVin}
        current_mileage={current_mileage}
        setCurrentMileage={setCurrentMileage}
        onRequest={onRequest}
        showRobReasonsDialog={showRobReasonsDialog}
        robComponents={robComponents}
        showNoInvoiceDialog={showNoInvoiceDialog}
        setShowNoInvoiceDialog={setShowNoInvoiceDialog}
        setInvoiceStatus={setInvoiceStatus}
        invoiceStatus={invoiceStatus}
        onChangeInvoiceStatus={onChangeInvoiceStatus}
        showNoInvoiceInfoDialog={showNoInvoiceInfoDialog}
        setShowNoInvoiceInfoDialog={setShowNoInvoiceInfoDialog}
        showRobRequestErrorsDialog={showRobRequestErrorsDialog}
        robErrors={robErrors}
        setShowRobRequestErrorsDialog={setShowRobRequestErrorsDialog}
        showRobTaskErrorsDialog={showRobTaskErrorsDialog}
        setShowRobTaskErrorsDialog={setShowRobTaskErrorsDialog}
        setShowRobReasonsDialog={setShowRobReasonsDialog}
        showDiscountInvoiceDialog={showDiscountInvoiceDialog}
        setShowDiscountInvoiceDialog={setShowDiscountInvoiceDialog}
        InvoiceServiceRequest={InvoiceServiceRequest}
        components={components}
        total_amount={total_amount}
        robContract={robContract}
        invoice={invoice}
        user_settings={user_settings}
        setShowSlaEdit={setShowSlaEdit}
        setShowTaskEdit={setShowTaskEdit}
        setShowCancelRobTaskDialog={setShowCancelRobTaskDialog}
        setShowOverlapInfoDialog={setShowOverlapInfoDialog}
        setShowMarkupInfoDialog={setShowMarkupInfoDialog}
        setShowDetails={setShowDetails}
        setShowAssignUserDialog={setShowAssignUserDialog}
        onActivateComponent={onActivateComponent}
        onDeleteComponent={onDeleteComponent}
        navigation={navigation}
        damage_id={damage_id}
        onDoRequest={onDoRequest}
        contract_ends={contract_ends}
        showTaskEdit={showTaskEdit}
        showSlaEdit={showSlaEdit}
        refresh={refresh}
        onTaskEdit={onTaskEdit}
        onSlaEdit={onSlaEdit}
      />

      <Footer
        damage_id={damage_id}
        editable={editable}
        status={overview.status}
        overview={overview}
        robContract={robContract}
        invoice={invoice}
        amount={total_amount}
        robErrors={robErrors}
        onDoRequest={onDoRequest}
        onRefresh={refresh}
        onInvoice={onInvoice}
        onShowRobRequestErrors={() => setShowRobRequestErrorsDialog(true)}
        onChangeTab={onChangeTab}
      />
      {renderDamageOverlays({
        showTaskEdit,
        setShowTaskEdit,
        overview,
        vin_number,
        damage_id,
        onTaskEdit,
        setShowRobReasonsDialog,
        showSlaEdit,
        setShowSlaEdit,
        onSlaEdit,
        showRequestDialog,
        setShowRequestDialog,
        partial_vin,
        setPartialVin,
        current_mileage,
        setCurrentMileage,
        onRequest,
        showRobReasonsDialog,
        robComponents,
        showNoInvoiceDialog,
        setShowNoInvoiceDialog,
        setInvoiceStatus,
        invoiceStatus,
        onChangeInvoiceStatus,
        showNoInvoiceInfoDialog,
        setShowNoInvoiceInfoDialog,
        showRobRequestErrorsDialog,
        robErrors,
        setShowRobRequestErrorsDialog,
        showRobTaskErrorsDialog,
        setShowRobTaskErrorsDialog,
        showDetails,
        setShowDetails,
        total_amount,
        showCancelRobTaskDialog,
        onDeleteComponent,
        refresh,
        setShowCancelRobTaskDialog,
        showOverlapInfoDialog,
        setShowOverlapInfoDialog,
        showMarkupInfoDialog,
        setShowMarkupInfoDialog,
        showAssignUserDialog,
        setShowAssignUserDialog,
        onUpdateRequest,
        showDiscountInvoiceDialog,
        setShowDiscountInvoiceDialog,
        InvoiceServiceRequest,
      })}
    </>
  );
};
DamageTabScrollContent.propTypes = {
  dimensions: PropTypes.object,
  overview: OverviewShape.isRequired,
  editable: PropTypes.bool,
  read_only: PropTypes.bool,
  vin_part: PropTypes.string,
  vin_number: PropTypes.string,
  edit_vin: PropTypes.object,
  setEditVIN: PropTypes.func,
  car: PropTypes.object,
  editKilometrage: PropTypes.any,
  setEditKilometrage: PropTypes.func,
  onUpdateRequest: PropTypes.func,
  onUpdateCar: PropTypes.func,
  onUpdateDamageRequest: PropTypes.func,
  setCar: PropTypes.func,
  edit_invoiceNumber: PropTypes.object,
  setEditInvoiceNumber: PropTypes.func,
  editDateTime: PropTypes.object,
  setEditDateTime: PropTypes.func,
  editDamageCaseNumber: PropTypes.any,
  setEditDamageCaseNumber: PropTypes.func,
  robComponents: PropTypes.object,
  setShowNoInvoiceDialog: PropTypes.func,
  robErrors: PropTypes.array,
  setShowRobTaskErrorsDialog: PropTypes.func,
  setShowRobReasonsDialog: PropTypes.func,
  components: PropTypes.array,
  total_amount: PropTypes.number,
  robContract: PropTypes.object,
  invoice: InvoiceShape,
  user_settings: PropTypes.object,
  setShowSlaEdit: PropTypes.func,
  setShowTaskEdit: PropTypes.func,
  setShowCancelRobTaskDialog: PropTypes.func,
  setShowOverlapInfoDialog: PropTypes.func,
  setShowMarkupInfoDialog: PropTypes.func,
  setShowDetails: PropTypes.func,
  setShowAssignUserDialog: PropTypes.func,
  onActivateComponent: PropTypes.func,
  onDeleteComponent: PropTypes.func,
  navigation: PropTypes.object,
  damage_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDoRequest: PropTypes.func,
  contract_ends: PropTypes.bool,
  refresh: PropTypes.func,
};
DamageTabBody.propTypes = {
  dimensions: PropTypes.object,
  focused: PropTypes.bool,
  onChangeTab: PropTypes.func,
};
const DamageTab = withDimensions(DamageTabBody);
export default DamageTab;
